// Dependencies
import { createFileRoute } from "@tanstack/react-router";
import { LangLink as Link } from "@/components/LangLink";
import { PageHero, PageShell } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { ApplicationCard } from "@/components/ApplicationCard";
import { ImageTextSection } from "@/components/ImageTextSection";
import { FeatureGrid } from "@/components/FeatureGrid";
import { CtaSection } from "@/components/CtaSection";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

// Icons
import {
  Rocket,
  Satellite,
  Orbit,
  Crosshair,
  RotateCcw,
  Puzzle,
  SlidersHorizontal,
  Cpu,
  Cog,
  Zap,
} from "lucide-react";

// Images
import heroVideo from "@/assets/video/reaction-control.mp4";
import rcs1 from "@/assets/rcs1.png";
import rcs2 from "@/assets/rcs2.png";
import rcs3 from "@/assets/rcs3.jpg";
import rcsRender from "@/assets/lancamento-foguete.png";
import application1 from "@/assets/application1.png";
import application2 from "@/assets/application2.png";
import application3 from "@/assets/application3.png";
import application4 from "@/assets/application4.png";
import ctaImage from "@/assets/cta.png";

export const Route = createFileRoute("/$lang/products/reaction-control-system")({
  head: () => ({
    meta: [
      { title: i18n.t("products/reaction-control-system:metaTitle", "Reaction Control Systems (RCS) — BIZU Space") },
      {
        name: "description",
        content: i18n.t("products/reaction-control-system:metaDesc",
          "BIZU Space is developing Reaction Control System (RCS) components to enable precise attitude control, rate damping, and maneuvers for launch vehicles and spacecraft."
        ),
      },
      { property: "og:title", content: i18n.t("products/reaction-control-system:metaTitle", "Reaction Control Systems (RCS) — BIZU Space") },
      {
        property: "og:description",
        content: i18n.t("products/reaction-control-system:metaOgDesc",
          "Modular thrusters, valves, and HTP attitude control systems engineered for upper stages and satellite platforms."
        ),
      },
      { property: "og:url", content: "/products/reaction-control-system" },
    ],
    links: [{ rel: "canonical", href: "https://bizu.space/products/reaction-control-system" }],
  }),
  component: ReactionControlSystem,
});

function ReactionControlSystem() {
  const { t } = useTranslation("products/reaction-control-system");

  const engineeringAreas = [
    {
      title: t("areas.a1Title", "Modular Thruster Assemblies"),
      description: t("areas.a1Desc", "Configurable thruster layouts for rotational and translational control."),
      icon: Rocket,
    },
    {
      title: t("areas.a2Title", "Feed System Components"),
      description: t("areas.a2Desc",
        "Development of valves, lines, regulators and supporting propulsion hardware for compact control architectures."
      ),
      icon: Cog,
    },
    {
      title: t("areas.a3Title", "Attitude Control Integration"),
      description: t("areas.a3Desc",
        "Subsystem concepts designed to work alongside guidance, navigation and control logic."
      ),
      icon: Crosshair,
    },
    {
      title: t("areas.a4Title", "Scalable Vehicle Integration"),
      description: t("areas.a4Desc",
        "Architectures adaptable to upper stages, orbital vehicles and satellite platforms."
      ),
      icon: Puzzle,
    },
    {
      title: t("areas.a5Title", "Responsive Operations Mindset"),
      description: t("areas.a5Desc",
        "Focused on practical aerospace integration, simplified operations and mission-oriented flexibility."
      ),
      icon: Zap,
    },
    {
      title: t("areas.a6Title", "Future-Ready Propulsion Building Blocks"),
      description: t("areas.a6Desc",
        "A technology path that supports the evolution from component development to complete control subsystems."
      ),
      icon: Cpu,
    },
  ];

  const rcsApplications = [
    {
      title: t("apps.a1Title", "Launch Vehicles"),
      description: t("apps.a1Desc",
        "Pitch, yaw and roll control in spaceflight phases, rate damping, stage orientation and support for upper-stage maneuver sequences."
      ),
      icon: Rocket,
      image: application1,
    },
    {
      title: t("apps.a2Title", "Satellites & Cubesats"),
      description: t("apps.a2Desc",
        "Attitude control, station-keeping, orbit adjustment, detumbling support and maneuver execution for spacecraft systems."
      ),
      icon: Satellite,
      image: application2,
    },
    {
      title: t("apps.a3Title", "Orbital Vehicles"),
      description: t("apps.a3Desc",
        "Support for transfer, phasing, re-orbiting and precise in-space control tasks with propulsion-based maneuver capability and enhanced operational flexibility."
      ),
      icon: Orbit,
      image: application3,
    },
    {
      title: t("apps.a4Title", "Advanced Platforms"),
      description: t("apps.a4Desc",
        "Modular control architectures for compact, responsive and mission-specific propulsion subsystems across aerospace systems."
      ),
      icon: Crosshair,
      image: application4,
    },
  ];

  const mattersBulletsList = [
    {
      icon: Crosshair,
      title: t("mattersBullets.b1", "Precise attitude control during coast phases."),
    },
    {
      icon: RotateCcw,
      title: t("mattersBullets.b2", "Roll, pitch and yaw control outside the atmosphere"),
    },
    {
      icon: Rocket,
      title: t("mattersBullets.b3", "Orbital adjustment and maneuver execution"),
    },
    {
      icon: Puzzle,
      title: t("mattersBullets.b4", "Scalable integration across different platforms"),
    },
    {
      icon: SlidersHorizontal,
      title: t("mattersBullets.b5", "Flexible mission design for launchers and spacecraft."),
    },
  ];

  return (
    <PageShell>
      <PageHero
        title={t("heroTitle", "REACTION CONTROL SYSTEMS")}
        description={t("heroDesc", "A modular reaction control architecture in development for launch vehicles and spacecraft. Designed to enable precise attitude control, rate damping and maneuver capability through scalable propulsion components and integration-ready subsystems.")}
        video={heroVideo}
      />

      <ImageTextSection
        title={t("controlTitle", "Designed for Control Beyond Main Propulsion")}
        image={rcs1}
        description={
          <>
            <p>
              {t("controlDesc1", "Launch vehicles and spacecraft require precise control even when the main engine is not the right tool.")}
            </p>
            <p className="mt-5">
              {t("controlDesc2", "BIZU Space is developing modular Reaction Control System components intended for upper stages, orbital vehicles, satellites and other spacecrafts — enabling architectures for pitch, yaw and roll control, attitude stabilization and in-space maneuvering. A good example is the Souyz spacecraft that uses a docking system based on Hydrogen peroxide.")}
            </p>
          </>
        }
      />
      <ImageTextSection
        title={t("modularTitle", "Built as a Modular Capability")}
        image={rcs2}
        variant="background"
        description={
          <>
            <p>
              {t("modularDesc1", "Rather than a single fixed configuration, our approach is centered on modular building blocks that can be adapted to different mission profiles and vehicle or satellite classes.")}
            </p>
            <p className="mt-5">
              {t("modularDesc2", "This development path is focused on creating flexible propulsion and control elements that can support both launch vehicle control needs and satellite maneuvering applications.")}
            </p>
          </>
        }
      />

      {/* SECTION 4: WHY IT MATTERS */}
      <section className="relative overflow-hidden py-32 text-white">
        {/* Autoplay video background */}
        <img src={rcs3} alt="Orbit view" className="absolute inset-0 w-full h-full object-cover" />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/85" />

        <div className="relative container-bizu z-10">
          <Reveal effect="scale" className="mx-auto max-w-3xl text-center mb-20">
            <h2 className="display mt-5 text-4xl uppercase md:text-5xl">{t("mattersTitle", "WHY IT MATTERS")}</h2>
            <p className="mt-6 text-lg text-white/70 leading-relaxed">
              {t("mattersDesc1", "The space exploration market is currently increasing with more need to move in space. These movements include orbital changes, maneuvers to avoid space debris, docking to other space systems, and high precision control maneuvers.")}
            </p>
            <p className="mt-6 text-lg text-white/70 leading-relaxed">
              {t("mattersDesc2", "A modular RCS architecture expands propulsion capability by supporting:")}
            </p>
          </Reveal>

          <Reveal stagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
            {mattersBulletsList.map((item) => {
              const Icon = item.icon;

              return (
                <div
                  key={item.title}
                  className="group relative overflow-hidden rounded-2xl border border-white/10 bg-black/60 p-6 backdrop-blur-md transition duration-500 hover:-translate-y-1 hover:bg-black/80"
                >
                  <div className="relative flex min-h-[180px] flex-col items-center justify-center text-center">
                    <Icon className="mb-6 h-8 w-8 text-white" />
                    <h3 className="text-lg leading-relaxed text-white">{item.title}</h3>
                  </div>
                </div>
              );
            })}
          </Reveal>
        </div>
      </section>

      {/* SECTION 5: KEY DEVELOPMENT AREAS */}
      <section className="relative overflow-hidden bg-black py-28 text-white border-b border-white/5">
        <div className="container-bizu">
          <Reveal effect="up" className="mx-auto max-w-3xl text-center mb-16">
            <span className="section-tag justify-center">Engineering</span>
            <h2 className="display mt-5 text-4xl uppercase md:text-5xl">{t("devAreasTitle", "KEY DEVELOPMENT AREAS")}</h2>
          </Reveal>

          <FeatureGrid items={engineeringAreas} />

          <Reveal effect="scale">
            <div className="relative mt-12 h-[300px] md:h-[450px] overflow-hidden rounded-3xl border border-white/15">
              <img
                src={rcsRender}
                alt="BIZU Space launch vehicle mission"
                className="w-full h-full object-cover opacity-85"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 6: APPLICATIONS */}
      <section className="relative overflow-hidden bg-[#070707] py-28 text-white">
        <div className="container-bizu">
          <Reveal effect="up" className="mb-16 text-center">
            <span className="section-tag justify-center">Applications</span>
            <h2 className="display mt-5 text-4xl uppercase md:text-6xl">{t("appsTitle", "Mission Applications")}</h2>
          </Reveal>

          <Reveal stagger className="grid gap-6 md:grid-cols-2">
            {rcsApplications.map((item, idx) => (
              <ApplicationCard
                key={item.title}
                title={item.title}
                description={item.description}
                image={item.image}
                icon={item.icon}
                index={idx}
              />
            ))}
          </Reveal>
        </div>
      </section>

      <ImageTextSection
        title={t("integrationTitle", "Engineered for Flexible Integration")}
        image={rcs2}
        imagePosition="right"
        description={
          <>
            <p>
              {t("integrationDesc1", "The goal is not only to develop thrusters, but to advance a modular control capability that can be integrated into broader aerospace systems.")}
            </p>
            <p className="mt-5">
              {t("integrationDesc2", "This includes the development of components and architectures that can be arranged according to mission needs, packaging constraints and control requirements.")}
            </p>
          </>
        }
      />
      <CtaSection
        image={ctaImage}
        title={t("ctaTitle", "Precision Control. Modular by Design.")}
        subtitle={t("ctaSubtitle", "BIZU Space is developing Reaction Control System capabilities as part of its broader propulsion roadmap — expanding from main propulsion into the control systems that make advanced aerospace missions possible.")}
        buttonText={t("ctaBtn", "Talk to Us")}
        href="/contact"
      />
    </PageShell>
  );
}
